import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Loader2, Home, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

interface WizardHomepageProps {
  onNext: () => void;
  onPrevious: () => void;
  siteId?: string;
}

interface GenerationJob {
  id: string;
  job_type: string;
  status: string;
  progress: number;
  total_items: number;
  error_message: string | null;
}

export function WizardHomepage({ onNext, onPrevious, siteId }: WizardHomepageProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    if (siteId) {
      fetchStatus();
      
      const channel = supabase
        .channel(`homepage-job-${siteId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'generation_jobs',
            filter: `site_id=eq.${siteId}`,
          },
          () => fetchStatus()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [siteId]);

  const fetchStatus = async () => {
    if (!siteId) return;

    try {
      const [jobRes, contentRes] = await Promise.all([
        supabase
          .from('generation_jobs')
          .select('id, job_type, status, progress, total_items, error_message')
          .eq('site_id', siteId)
          .eq('job_type', 'homepage')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('homepage_content')
          .select('id, draft_content')
          .eq('site_id', siteId)
          .maybeSingle(),
      ]);

      setJob(jobRes.data);
      setHasContent(!!contentRes.data?.draft_content);
    } catch (error) {
      console.error('Error fetching homepage status:', error);
    } finally {
      setLoading(false);
    }
  };

  const retryGeneration = async () => {
    if (!siteId) return;

    try {
      await supabase
        .from('generation_jobs')
        .delete()
        .eq('site_id', siteId)
        .eq('job_type', 'homepage')
        .eq('status', 'failed');

      await supabase
        .from('generation_jobs')
        .insert({ site_id: siteId, job_type: 'homepage', status: 'pending' });

      const session = await supabase.auth.getSession();
      if (session.data.session) {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-generation-jobs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {});
      }

      toast({ title: 'Homepage generation restarted' });
      fetchStatus();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isProcessing = job?.status === 'processing' || job?.status === 'pending';
  const isComplete = job?.status === 'completed' || hasContent;
  const isFailed = job?.status === 'failed';
  const progressPercent = job && job.total_items > 0 
    ? Math.round((job.progress / job.total_items) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              <CardTitle>Homepage Content</CardTitle>
            </div>
            {isComplete && <Badge variant="default">Ready</Badge>}
            {isProcessing && <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating</Badge>}
            {isFailed && <Badge variant="destructive">Failed</Badge>}
          </div>
          <CardDescription>
            Review and customize your homepage hero, about section, and featured content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-sm text-muted-foreground text-center">
                AI is generating your homepage content...
              </p>
            </div>
          )}

          {isFailed && (
            <div className="text-center space-y-3">
              <p className="text-sm text-destructive">{job?.error_message || 'Generation failed'}</p>
              <Button variant="outline" onClick={retryGeneration}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Generation
              </Button>
            </div>
          )}

          {isComplete && (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Your homepage content is ready. Click below to review and edit.
              </p>
              <Button onClick={() => window.open('/admin/pages/home', '_blank')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Edit Homepage Content
              </Button>
            </div>
          )}

          {!isProcessing && !isFailed && !isComplete && (
            <div className="text-center py-4 text-muted-foreground">
              Homepage content will be generated once you complete the previous steps.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button onClick={onNext} disabled={isProcessing}>
          {isProcessing ? 'Please wait...' : 'Continue'}
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
